import type { BoardRow } from "./thisWeekBoard";

/* THE CLIENTS ROSTER, REDRAWN IN OUR GRAMMAR (W72 step 1, Proposal 7's worked example).
 *
 * The rows are the SAME `BoardRow`s the This week board draws — built by `boardRows` from
 * `rosterData.ts` — so "who needs you" is one answer on both screens: the `Need you` chip here counts
 * exactly the clients the board's day line names. What this file adds is only what the roster does
 * that the board does not: a chip filter, a search, and three sorts. The copied table's Columns
 * picker, Hide filters, left radio rail and seven number columns are gone; the week line and the
 * Progress tab answer what those numbers asked. */

export type RosterChip = "all" | "active" | "invited" | "needYou" | "archived" | "watch";
/** The chips, in order. ⚠️ `watch` (W101b) is LAST and EXISTS ONLY WHEN SOMEBODY IS WATCHED — draw `rosterChips(counts)`. */
export const ROSTER_CHIPS: readonly RosterChip[] = ["all", "active", "invited", "needYou", "archived", "watch"];
export const CHIP_LABEL: Record<RosterChip, string> = {
  all: "All", active: "Active", invited: "Invited", needYou: "Need you", archived: "Archived", watch: "Watch",
};

/** W126 — a watched ROW's chip: `Watch N`, N = that client's watched movements; null at 0 (an unwatched row draws nothing). */
export function rowWatchChip(n: number): string | null {
  return n > 0 ? `${CHIP_LABEL.watch} ${String(n)}` : null;
}

/** The chips the page draws: every one, but `Watch N` only when N > 0 (Fable's W101b ruling). */
export function rosterChips(counts: Record<RosterChip, number>): RosterChip[] {
  // N2g — `Archived` (declined ∪ ended, the phone's chip) likewise only when somebody is in it.
  return ROSTER_CHIPS.filter((c) => (c !== "watch" || counts.watch > 0) && (c !== "archived" || counts.archived > 0));
}

/**
 * THE COLUMN NAMES, ONCE — for the roster AND the board, and the sort menu derives its words from them.
 * One fact, one name (the 2026-09-20 lesson: `Last Assigned Workout` above a `Next assigned` header, for
 * one value). `rosterOneLabel.test.ts` asserts no screen types these again.
 */
export const COLUMN = {
  client: "Client", week: "This week", needs: "Needs you", trained: "Trained", next: "Next assigned",
} as const;

export type RosterSort = "trained" | "name" | "next";
export const ROSTER_SORTS: readonly RosterSort[] = ["trained", "name", "next"];
export const SORT_LABEL: Record<RosterSort, string> = {
  trained: `last ${COLUMN.trained.toLowerCase()}`, name: "name", next: COLUMN.next.toLowerCase(),
};

/** `watched` — the client ids carrying a two-week watch line (`reviewQueue.watchByClient`); absent = nobody. */
function inChip(r: BoardRow, chip: RosterChip, watched: ReadonlySet<string>): boolean {
  switch (chip) {
    case "all": return !r.archived;
    case "active": return !r.invited;
    case "invited": return r.invited && !r.archived;
    case "archived": return r.archived;
    case "needYou": return !r.invited && r.needs.length > 0;
    case "watch": return !r.invited && r.clientId !== null && watched.has(r.clientId);
  }
}

export function chipCounts(rows: readonly BoardRow[], watched: ReadonlySet<string> = new Set()): Record<RosterChip, number> {
  return {
    all: rows.filter((r) => inChip(r, "all", watched)).length,
    active: rows.filter((r) => inChip(r, "active", watched)).length,
    invited: rows.filter((r) => inChip(r, "invited", watched)).length,
    needYou: rows.filter((r) => inChip(r, "needYou", watched)).length,
    archived: rows.filter((r) => inChip(r, "archived", watched)).length,
    watch: rows.filter((r) => inChip(r, "watch", watched)).length,
  };
}

/**
 * The rows on screen: the chip, then the search (name, case-insensitive), then the sort — and the
 * invited ALWAYS after the active whatever the sort, because a person who has not joined has no week
 * to compare. Nulls sort last in every direction: "nothing trained" is not the oldest training.
 */
export function rosterView(
  rows: readonly BoardRow[], chip: RosterChip, query: string, sort: RosterSort, watched: ReadonlySet<string> = new Set(),
): BoardRow[] {
  const q = query.trim().toLowerCase();
  const shown = rows.filter((r) => inChip(r, chip, watched) && (q === "" || r.name.toLowerCase().includes(q)));
  const key = (r: BoardRow): string | null =>
    sort === "trained" ? r.lastTrainedISO : sort === "next" ? r.firstAfterWeekISO : r.name.toLowerCase();
  return shown.sort((a, b) => {
    if (a.invited !== b.invited) return a.invited ? 1 : -1;
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    const ka = key(a);
    const kb = key(b);
    if (ka !== kb) {
      if (ka === null) return 1;
      if (kb === null) return -1;
      // Last trained: most recent first. Name and next assigned: earliest / A first.
      const cmp = ka < kb ? -1 : 1;
      return sort === "trained" ? -cmp : cmp;
    }
    return a.name.localeCompare(b.name);
  });
}

/** `?sort=` read back — anything it does not know is the default, never an error. */
export function rosterSortFrom(q: Record<string, string>): RosterSort {
  const s = q["sort"];
  return s === "name" || s === "next" || s === "trained" ? s : "trained";
}

/** The foot: `6 clients · 4 training · a client joins with your link, no email needed`. */
export function rosterFoot(rows: readonly BoardRow[]): string {
  const active = rows.filter((r) => !r.invited).length;
  const n = rows.filter((r) => !r.archived).length;   // N2g — a declined or ended row is not one of your clients
  return `${n} ${n === 1 ? "client" : "clients"} · ${active} training · a client joins with your link, no email needed`;
}
